import { Component, TemplateRef, ViewChild, OnInit, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject, catchError, debounceTime, distinctUntilChanged, firstValueFrom, from, map, merge, Observable, of, startWith, switchMap, takeUntil, throwError, Subscription } from 'rxjs';
import { BaseComponent } from '../../../core/components/base/base.component';
import { FeedbackService } from '../../../core/services/feedback.service';
import { UserService } from '../../../core/services/user.service';
import { GroupService } from '../../../core/services/group.service';
import { FormControl } from '@angular/forms';
import { Speciality } from 'src/app/core/models/speciality.model';
import { HttpClient } from '@angular/common/http';
import * as _ from 'lodash';
import { SPECIALITIES, medpraxTariffType } from 'src/app/core/data/app.data';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { MedpraxService, PlanOption, Scheme } from '../../../core/invoicing/medprax.invoicing.service';
import { PatientFinancalBillingService } from 'src/app/core/services/patient-financal-billing.service';
import { ProcedureCode } from 'src/app/core/services/app-data.service';
import { ConsumableOverride } from 'src/app/shared/components/consumable-override-selector/consumable-override-selector.component';

@Component({
  selector: 'app-accounting-settings',
  templateUrl: './accounting-settings.component.html',
  styleUrls: ['./accounting-settings.component.scss']
})
export class AccountingSettingsComponent extends BaseComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  speciality: string = '';
  doctorBaseRateControl: FormControl = new FormControl();
  filteredDoctorBaseRates!: Observable<any[]>;
  private doctorBaseRateListUpdated = new BehaviorSubject<any[]>([]);
  doctorBaseRateList: any[] = [];
  selectedDoctorBaseRate?: any;
  tariffRates: any[] = [];
  selectedSpeciality?: Speciality;
  isNetworkDoctor: boolean = true;
  contractedRates: any[] = [];
  isDefaultRate: boolean = false;
  editingTariffs: any = {}; // Object to store Tariff Form
  editingBusinessDetails: any = {}; // Object to store Business Details Form
  editingYocoDetails: any = {}; // Object to store YOCO INTEGRATION Details Form
  group: any = {}; // Object to store selected Group
  private apiUrl = 'api/accounting';
  @ViewChild('addTarrifRatesDialog') addTarrifRatesDialog?: TemplateRef<any>;
  @ViewChild('addBusinessDetailsDialog') addBusinessDetailsDialog?: TemplateRef<any>;
  @ViewChild('yocoIntegrationDialog') yocoIntegrationDialog?: TemplateRef<any>;
  @ViewChild('medicalAidDialog') medicalAidDialog?: TemplateRef<any>;
  patientSchemeControl: FormControl = new FormControl('');
  filteredPatientSchemes!: Observable<Scheme[]>;
  schemes: Scheme[] = [];
  planOptions: PlanOption[] = [];
  schemeFilter = '';
  selectedPatientScheme?: Scheme;
  showPublicKey = false;
  showSecretKey = false;

  constructor(
    fs: FeedbackService,
    md: MatDialog,
    private userService: UserService,
    private groupService: GroupService,
    private medpraxService: MedpraxService,
    private http: HttpClient,
    private patientBillingService: PatientFinancalBillingService,
  ) {
    super(fs, md);
  }

  ngOnInit(): void {
    // Initialize filtered base rates
    this.bindRateFilterControl();

    // Load schemes
    this.loadSchemes();

    // Initialize patient scheme autocomplete
    this.filteredPatientSchemes = this.patientSchemeControl.valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((value) => {
        if (typeof value === 'string') {
          return this._filterSchemes(value);
        } else if (value && value.code) {
          return of([value]);
        } else {
          return of([]);
        }
      })
    );

    this.subscriptions.push(
      this.groupService.selectedGroup$.subscribe(selectedGroup => {
        if (selectedGroup) {
          this.group = selectedGroup;

          this.speciality = selectedGroup.groupSpeciality || '';
          if (this.speciality) {
            this.onSpecialitySelected(this.speciality, false)
          }
          //console.log("Speciality: ", this.speciality)
          // Initialize editing tariffs
          this.editingTariffs = {
            isNetworkDoctor: this.isNetworkDoctor,
            name: "",
            priceGroup: "",
            autoPatientScheme: "",
            planOptions: "",
            modifier: "",
            isDefaultRate: this.isDefaultRate,
            groupId: "",
          };

          // Initialize business details
          this.editingBusinessDetails = {
            companyName: this.group.companyName || "",
            vatNumber: this.group.vatNumber || "",
            registrationNumber: this.group.registrationNumber || "",
            address: this.group.address || "",
            bankingDetails: this.group.bankingDetails || { accountName: '', bankName: '', accountType: '', accountNumber: '', branchName: '', branchCode: '' },
            accountsPhone: this.group.accountsPhone || this.group.practicePhone || "",
            accountsEmail: this.group.accountsEmail || this.group.practiceEmail || "",
          };

          // Initialize YOCO details
          this.editingYocoDetails = {
            yocoSecretKey: this.group.yocoSecretKey || "",
            yocoPublicKey: this.group.yocoPublicKey || "",
          };

          this.getTariffRates(selectedGroup.id);


        }
      })
    );

    // Subscribe to form control changes to update editingTariffs
    this.doctorBaseRateControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        if (value) {
          this.editingTariffs.priceGroup = value;
        }
      });

    this.patientSchemeControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        if (value) {
          this.editingTariffs.autoPatientScheme = value;
        }
      });
  }

  override ngOnDestroy() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    super.ngOnDestroy();
  }

  private _filterSchemes(value: string): Observable<Scheme[]> {
    const filterValue = value.toLowerCase();
    const filteredSchemes = this.schemes.filter(scheme =>
      scheme.name.toLowerCase().includes(filterValue)
    );
    return of(filteredSchemes);
  }

  private bindRateFilterControl() {
    this.filteredDoctorBaseRates = this.doctorBaseRateControl.valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(value => {
        if (typeof value === 'string') {
          return this._filterBaseRates(value);
        } else if (value && value.code) {
          return of([value]);
        } else {
          return of([]);
        }
      })
    );
  }

  onPatientSchemeSelected(event: any) {
    const selectedScheme = event;
    return this.medpraxService.getPlanOptions(selectedScheme.code).subscribe({
      next: (data) => {
        this.planOptions = data;
        //console.log("Plan Options: ", this.planOptions);
      },
      error: (err) => {
        console.error("Failed to load plan options", err);
      }
    });
  }

  /** Patient: Ensure valid scheme selection */
  onPatientSchemeBlur(): void {
    if (typeof this.patientSchemeControl.value === 'string') {
      this.patientSchemeControl.setValue('');
      this.selectedPatientScheme = undefined;
    }
  }

  displayBaseRate(pg: any): string {
    return pg ? `${pg.code} - ${pg.description}` : '';
  }

  /** Display helper for schemes */
  displayScheme(scheme: Scheme): string {
    return scheme ? scheme.name : '';
  }

  loadSchemes() {
    this.medpraxService.searchSchemes(this.schemeFilter)
      .subscribe({
        next: data => this.schemes = data,
        error: err => console.error('Failed to load schemes', err)
      });
  }

  onSpecialitySelected(speciality: any, isContract: boolean): void {
    //console.log('SELECTED SPECIALITY: ', speciality);
    this.selectedSpeciality = speciality;

    // Clear prior base-rate selection
    this.doctorBaseRateControl.setValue('');
    this.selectedDoctorBaseRate = undefined;
    this.doctorBaseRateList = [];

    const matchedSpeciality = SPECIALITIES.find(s =>
      s.value === speciality || s.value === speciality.value
    );

    const disciplineCode = matchedSpeciality?.discipline;
    //console.log("Details: ", matchedSpeciality);
    //console.log("Discipline: ", disciplineCode);
    //console.log("Is Contract? : ", isContract);

    if (disciplineCode) {
      // Fetch price groups for the chosen discipline
      this.getPriceGroups(disciplineCode, isContract).subscribe({
        next: (result: any) => {
          //console.log('Price groups for discipline:', result);
          this.doctorBaseRateList = result;
          this.doctorBaseRateListUpdated.next(result);
          this.doctorBaseRateControl.setValue('');
          this.bindRateFilterControl();
        },
        error: (err) => {
          console.error('Failed to get price groups:', err);
        }
      });
    } else {
      console.warn('No matching discipline code found for selected speciality.');
    }
  }

  onDoctorBaseRateSelected(priceGroup: any): void {
    this.selectedDoctorBaseRate = priceGroup;
  }

  private _filterBaseRates(value: string): Observable<any[]> {
    const filterValue = value.toLowerCase();
    //console.log('Filtering base rates with value:', filterValue);
    //console.log('Available base rates:', this.doctorBaseRateList);

    const filtered = this.doctorBaseRateList.filter(pg =>
    (pg.code?.toLowerCase().includes(filterValue) ||
      pg.description?.toLowerCase().includes(filterValue))
    );

    //console.log('Filtered results:', filtered);
    return of(filtered);
  }

  openAddTarrifRatesDialog(event: MouseEvent): void {
    this.onSpecialitySelected(this.speciality, false);
    event.stopPropagation();
    this.editingTariffs = { isNetworkDoctor: false, name: '', modifier: 100, isDefaultRate: this.tariffRates?.length > 0 ? false : true };
    this.doctorBaseRateControl.setValue('');
    this.dialog.open(this.addTarrifRatesDialog!, { width: '500px' });
  }


  openAddBusinessDetailsDialog(event: MouseEvent): void {
    event.stopPropagation();
    this.dialog.open(this.addBusinessDetailsDialog!, { width: '500px' });
  }

  openYocoIntegrationDialog(event: MouseEvent): void {
    event.stopPropagation();
    this.dialog.open(this.yocoIntegrationDialog!, { width: '500px' });
  }

  openMedicalShemeDialog(event: MouseEvent): void {
    event.stopPropagation();
    this.dialog.open(this.medicalAidDialog!, { width: '500px' });
  }

  onTariffOverrideCodesChanged(codes: ProcedureCode[]) {

    const sub = this.groupService.updateGroupTariffOverrides(this.group.id, codes)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response: any) => {
          sub.unsubscribe();
        },
        (error) => {
          console.error('Error updating tariff price overrides:', error);
          this.showToastMessage('There was an error updating your tariff price overrides. Please try again.');
          sub.unsubscribe();
        }
      );
  }

  onConsumableOverridesChanged(overrides: ConsumableOverride[]) {
    const sub: Subscription = this.groupService.updateGroupTariffOverrides(this.group.id, undefined, overrides)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        () => sub.unsubscribe(),
        (error) => {
          console.error('Error updating consumable price overrides:', error);
          this.showToastMessage('There was an error updating consumable price overrides. Please try again.');
          sub.unsubscribe();
        }
      );
  }

  cancelEdit(): void {
    this.dialog.closeAll();
  }

  getPriceGroups(disciplineCode: string, isContract: boolean): Observable<any[]> {
    const rateType = medpraxTariffType(disciplineCode);
    return this.medpraxService.listPriceGroups(rateType, disciplineCode, isContract).pipe(
      map(result => {
        //console.log('Price groups for discipline:', result);
        return result;
      }),
      catchError(error => {
        console.error('Error fetching price groups:', error);
        this.showToastMessage('Error fetching price groups. Please try again.');
        return throwError(() => error);
      })
    );
  }

  onNetworkToggle(event: MatSlideToggleChange): void {
    this.isNetworkDoctor = event.checked;
    this.editingTariffs.isNetworkDoctor = event.checked;
    if (this.selectedSpeciality) {
      this.onSpecialitySelected(this.selectedSpeciality, event.checked);
    }
  }

  onDefaultToggle(event: MatSlideToggleChange): void {
    this.isDefaultRate = event.checked;
    this.editingTariffs.isDefaultRate = event.checked;
  }

  saveTariffRates(): void {
    if (!this.editingTariffs.priceGroup) {
      this.showToastMessage('Please select a price group');
      return;
    }

    const requestBody = {
      ...this.editingTariffs,
      groupId: this.group.id,
      name: this.editingTariffs.name,
      priceGroup: this.editingTariffs.priceGroup,
      autoPatientScheme: this.editingTariffs.autoPatientScheme?.code || '',
      planOptions: this.editingTariffs.planOptions || '',
      modifier: this.editingTariffs.modifier || '100',
      isDefaultRate: this.editingTariffs.isDefaultRate || false,
      isNetworkDoctor: this.editingTariffs.isNetworkDoctor || false,
    };

    this.saveTariffRate(requestBody);
  }

  saveTariffRate(requestBody: any) {
    this.http.post(`${this.apiUrl}/tariff-rates`, requestBody)
      .subscribe({
        next: (response) => {
          //console.log('Tariff rate saved successfully:', response);
          this.showToastMessage('Tariff rate saved successfully');
          this.dialog.closeAll();
          this.getTariffRates(this.group.id);
          this.resetEditingTariffs();
        },
        error: (error) => {
          console.error('Error saving tariff rate:', error);
          this.showToastMessage('Error saving tariff rate');
        }
      });
  }

  resetEditingTariffs(): void {
    this.editingTariffs = {
      isNetworkDoctor: this.isNetworkDoctor,
      name: "",
      priceGroup: "",
      autoPatientScheme: "",
      planOptions: "",
      modifier: "",
      isDefaultRate: this.isDefaultRate,
      groupId: "",
    };
    this.doctorBaseRateControl.setValue('');
    this.patientSchemeControl.setValue('');
  }

  getTariffTooltip(tariff: any): string {
    return `Default Rate: ${tariff.isDefaultRate ? 'Yes' : 'No'}\nContract Doctor: ${tariff.isNetworkDoctor ? 'Yes' : 'No'}`;
  }

  trackByTariffId(index: number, tariff: any): number {
    return tariff.id;
  }

  editTariff(tariff: any): void {
    this.editingTariffs = {
      ...tariff,
      priceGroup: this.doctorBaseRateList.find(pg => pg.code === tariff.priceGroup),
      autoPatientScheme: this.schemes.find(s => s.code === tariff.autoPatientScheme),
      isNetworkDoctor: tariff.isNetworkDoctor,
      isDefaultRate: tariff.isDefaultRate,
      groupId: this.group.id,
    };

    this.doctorBaseRateControl.setValue(this.editingTariffs.priceGroup);
    this.patientSchemeControl.setValue(this.editingTariffs.autoPatientScheme);

    this.dialog.open(this.addTarrifRatesDialog!, { width: '500px' });
  }

  async removeTariff(tariff: any): Promise<void> {
    const confirmed = await this.showConfirmationDialog(
      'Remove Tariff Rate',
      'Are you sure you want to remove this tariff rate?',
      undefined,
      'Cancel',
      'Remove',
      false
    );

    if (confirmed) {
      this.http.delete(`${this.apiUrl}/tariff-rates/${tariff.id}`)
        .subscribe({
          next: () => {
            this.showToastMessage('Tariff rate removed successfully');
            this.getTariffRates(this.group.id);
          },
          error: (error) => {
            console.error('Error removing tariff rate:', error);
            this.showToastMessage('Error removing tariff rate');
          }
        });
    }
  }

  saveBusinessDetails(): void {
    const requestBody = {
      ...this.editingBusinessDetails,
      groupId: this.group.id,
    };

    this.groupService.updateBusinessDetails(requestBody)
      .subscribe({
        next: (response) => {
          //console.log('Business details saved successfully:', response);
          this.showToastMessage('Business details saved successfully');
          this.dialog.closeAll();
        },
        error: (error) => {
          console.error('Error saving business details:', error);
          this.showToastMessage('Error saving business details');
        }
      });


  }

  saveYocoDetails() {
    const requestBody = {
      ...this.editingYocoDetails,
      groupId: this.group.id,
    };

    this.groupService.updateYocoDetails(requestBody)
      .subscribe({
        next: (response) => {
          //console.log('YOCO details saved successfully:', response);
          this.showToastMessage('YOCO details saved successfully');

          // Register webhook after saving credentials
          this.registerYocoWebhook();

          this.dialog.closeAll();
        },
        error: (error) => {
          console.error('Error saving YOCO details:', error);
          this.showToastMessage('Error saving YOCO details');
        }
      });
  }

  registerYocoWebhook() {
    this.groupService.registerYocoWebhook(this.group.id).subscribe({
      next: (response) => {
        //console.log('Webhook registered successfully:', response);
        this.showToastMessage('Yoco webhook registered successfully');
        if (response.success) {
          const group = this.group;
          if (group) {
            group.yocoWebhookId = response.webhook.id;
            this.groupService.setSelectedGroup(group);
          }
        }
      },
      error: (error) => {
        console.error('Error registering webhook:', error);
        // Don't show error to user as this is not critical for basic functionality
      }
    });
  }

  saveMedicalAidDetails() {
    // Implementation for saving medical aid details
  }

  private getTariffRates(groupId: string) {
    this.medpraxService.getTariffRatesByGroup(groupId).subscribe(
      (response) => {
        if (!response || !response.tariffRates) {
          console.error("❌ No tariff rates found.");
          return;
        }
        this.tariffRates = response.tariffRates;
        //console.log("✅ Tariff Rates Fetched:", this.tariffRates);
      },
      (error) => {
        console.error("❌ Error fetching tariff rates:", error);
        if (error.status === 200 && typeof error.error.text === 'string') {
          console.error("❌ API returned HTML instead of JSON.");
        }
      }
    );
  }
}


